/**
 * Mock data for the cashier app.
 *
 * Mirrors the constants from prototypes/matix-pos_6.html. Used until the
 * corresponding pos-api endpoints land:
 *   - PRODUCTS / CATEGORIES → replaced by live catalog API (already wired)
 *   - MOD_GROUPS → will become per-item modifier endpoint
 *   - CUSTOMERS → will become customer search/CRM endpoint
 *   - PAYMENT_METHODS → already partially in voucher type mapping
 *
 * Until then, the cart/modifier/customer/payment flows operate on this
 * data so the cashier UI is fully exercisable end-to-end.
 */

import { safePrecision } from '@matix/utils';
import { moneyPrecision } from '../store/currency';

export interface MockCategory {
  id: string;
  name: string;
  icon: string;
}

export interface MockProduct {
  id: number;
  name: string;
  nameAr: string;
  price: number;
  cat: string;
  emoji: string;
  bg: string;
  station: string;
  /**
   * The item's own photo as a data URL (pos_item_images, same value the catalog
   * tile paints). Null/absent when the tenant has no photo for the item or has
   * IsShowProductImage off — the cart then falls back to `emoji`/`bg`, which are
   * category-level and therefore identical for every item in a category.
   */
  image?: string | null;
  /** Item code / item number (Inv_Items.Code) — printed on the receipt when enabled. */
  code?: string | null;
  /** Item barcode (Inv_Items.GlobalBarcode) — printed on the receipt when enabled. */
  barcode?: string | null;
  /**
   * Inv_Items.RestockOnReturn. When true, a RETURN line for this item shows a
   * "Restock it" checkbox: unticking it makes the backend write a Code 61
   * Destruction voucher so the goods don't go back on the shelf. Items without
   * the flag are always restocked and get no checkbox.
   */
  restockOnReturn?: boolean;
  /**
   * Inv_Items.AllowDecimals — the item is sold by weight / fraction (0.5 kg,
   * 0.25 m). When true the cart accepts any quantity down to 0.001; when
   * false/absent the quantity is whole units with a floor of 1, as before.
   */
  allowDecimals?: boolean;
  /**
   * The Inv_Items_Units unit this line sells in (Inv_Receipt_d.Unit_ID). An
   * item with several POS units — a can sold by the piece AND by the carton —
   * gets one catalog tile per unit, and the unit rides on the line so the
   * server prices, converts stock and prints it as that unit. Absent on lines
   * built before units were carried; the server then uses the selling unit.
   */
  unitId?: number | null;
  /**
   * The unit's names, set ONLY for a multi-unit item: they are what the cart
   * line, the scanner sheet and the receipt show after the quantity, and a
   * single-unit item must look exactly as it always did.
   */
  unitName?: string | null;
  unitNameAr?: string | null;
  mods?: string[];
  combo?: boolean;
  tag?: string;
}

export interface ModifierOption {
  name: string;
  nameAr: string;
  price: number;
}

export interface ModifierGroup {
  title: string;
  titleAr: string;
  required: boolean;
  min: number;
  max: number;
  options: ModifierOption[];
}

export const MOCK_CATEGORIES: MockCategory[] = [
  { id: 'all', name: 'All', icon: '🍽️' },
  { id: 'starters', name: 'Starters', icon: '🥗' },
  { id: 'shawarma', name: 'Shawarma', icon: '🌯' },
  { id: 'burgers', name: 'Burgers', icon: '🍔' },
  { id: 'pizza', name: 'Pizza', icon: '🍕' },
  { id: 'mains', name: 'Mains', icon: '🍗' },
  { id: 'sides', name: 'Sides', icon: '🍟' },
  { id: 'drinks', name: 'Drinks', icon: '🥤' },
  { id: 'desserts', name: 'Desserts', icon: '🍰' },
  { id: 'combos', name: 'Combos', icon: '🎁' },
];

export const MOCK_PRODUCTS: MockProduct[] = [
  { id: 1, name: 'Chicken shawarma', nameAr: 'شاورما دجاج', price: 28, cat: 'shawarma', emoji: '🌯', bg: '#FEF3C7', station: 'Hot kitchen', mods: ['bread', 'addons', 'remove'] },
  { id: 2, name: 'Beef shawarma', nameAr: 'شاورما لحم', price: 34, cat: 'shawarma', emoji: '🌯', bg: '#FEE2E2', station: 'Hot kitchen', mods: ['bread', 'addons', 'remove'] },
  { id: 3, name: 'Mixed shawarma', nameAr: 'شاورما مشكل', price: 38, cat: 'shawarma', emoji: '🌯', bg: '#FED7AA', station: 'Hot kitchen', mods: ['bread', 'addons', 'remove'], tag: 'Popular' },
  { id: 4, name: 'Beef burger', nameAr: 'برجر لحم', price: 42, cat: 'burgers', emoji: '🍔', bg: '#FEF3C7', station: 'Hot kitchen', mods: ['cooking', 'addons', 'remove'] },
  { id: 5, name: 'Cheese burger', nameAr: 'برجر بالجبن', price: 46, cat: 'burgers', emoji: '🍔', bg: '#FEF3C7', station: 'Hot kitchen', mods: ['cooking', 'addons', 'remove'] },
  { id: 6, name: 'Double burger', nameAr: 'برجر مزدوج', price: 58, cat: 'burgers', emoji: '🍔', bg: '#FED7AA', station: 'Hot kitchen', mods: ['cooking', 'addons', 'remove'] },
  { id: 7, name: 'Chicken burger', nameAr: 'برجر دجاج', price: 38, cat: 'burgers', emoji: '🍔', bg: '#FEF3C7', station: 'Hot kitchen', mods: ['cooking', 'addons', 'remove'] },
  { id: 8, name: 'Margherita pizza', nameAr: 'بيتزا مارجريتا', price: 55, cat: 'pizza', emoji: '🍕', bg: '#FEE2E2', station: 'Hot kitchen', mods: ['size', 'toppings'] },
  { id: 9, name: 'Pepperoni pizza', nameAr: 'بيتزا بيبروني', price: 68, cat: 'pizza', emoji: '🍕', bg: '#FED7AA', station: 'Hot kitchen', mods: ['size', 'toppings'], tag: 'Top seller' },
  { id: 10, name: 'Veggie pizza', nameAr: 'بيتزا خضار', price: 52, cat: 'pizza', emoji: '🍕', bg: '#D1FAE5', station: 'Hot kitchen', mods: ['size', 'toppings'] },
  { id: 11, name: 'Caesar salad', nameAr: 'سلطة سيزر', price: 32, cat: 'starters', emoji: '🥗', bg: '#D1FAE5', station: 'Cold kitchen', mods: ['protein'] },
  { id: 12, name: 'Fattoush', nameAr: 'فتوش', price: 28, cat: 'starters', emoji: '🥗', bg: '#D1FAE5', station: 'Cold kitchen' },
  { id: 13, name: 'Hummus', nameAr: 'حمص', price: 22, cat: 'starters', emoji: '🥣', bg: '#FED7AA', station: 'Cold kitchen' },
  { id: 14, name: 'Grilled chicken', nameAr: 'دجاج مشوي', price: 65, cat: 'mains', emoji: '🍗', bg: '#FEF3C7', station: 'Grill', mods: ['sides'] },
  { id: 15, name: 'Lamb kebab', nameAr: 'كباب لحم', price: 78, cat: 'mains', emoji: '🍢', bg: '#FEE2E2', station: 'Grill', mods: ['sides'] },
  { id: 16, name: 'French fries', nameAr: 'بطاطس مقلية', price: 18, cat: 'sides', emoji: '🍟', bg: '#FEF3C7', station: 'Hot kitchen' },
  { id: 17, name: 'Onion rings', nameAr: 'حلقات بصل', price: 22, cat: 'sides', emoji: '🧅', bg: '#FEF3C7', station: 'Hot kitchen' },
  { id: 18, name: 'Garlic bread', nameAr: 'خبز بالثوم', price: 16, cat: 'sides', emoji: '🥖', bg: '#FEF3C7', station: 'Hot kitchen' },
  { id: 19, name: 'Mango juice', nameAr: 'عصير مانجو', price: 14, cat: 'drinks', emoji: '🥭', bg: '#FED7AA', station: 'Bar' },
  { id: 20, name: 'Mint lemonade', nameAr: 'ليمون بالنعناع', price: 12, cat: 'drinks', emoji: '🍋', bg: '#D1FAE5', station: 'Bar' },
  { id: 21, name: 'Cola', nameAr: 'كولا', price: 8, cat: 'drinks', emoji: '🥤', bg: '#FEE2E2', station: 'Bar' },
  { id: 22, name: 'Fresh orange', nameAr: 'برتقال طازج', price: 16, cat: 'drinks', emoji: '🍊', bg: '#FED7AA', station: 'Bar' },
  { id: 23, name: 'Water bottle', nameAr: 'مياه', price: 4, cat: 'drinks', emoji: '💧', bg: '#DBEAFE', station: 'Bar' },
  { id: 24, name: 'Arabic coffee', nameAr: 'قهوة عربية', price: 10, cat: 'drinks', emoji: '☕', bg: '#FED7AA', station: 'Bar' },
  { id: 25, name: 'Cheesecake', nameAr: 'تشيز كيك', price: 28, cat: 'desserts', emoji: '🍰', bg: '#FCE7F3', station: 'Cold kitchen' },
  { id: 26, name: 'Kunafa', nameAr: 'كنافة', price: 32, cat: 'desserts', emoji: '🥮', bg: '#FED7AA', station: 'Cold kitchen', tag: 'Signature' },
  { id: 27, name: 'Ice cream', nameAr: 'آيس كريم', price: 18, cat: 'desserts', emoji: '🍨', bg: '#FCE7F3', station: 'Cold kitchen' },
  { id: 28, name: 'Family meal', nameAr: 'وجبة عائلية', price: 189, cat: 'combos', emoji: '🎁', bg: '#FEF3C7', station: 'Hot kitchen', combo: true, tag: 'Save 15%' },
  { id: 29, name: 'Burger combo', nameAr: 'وجبة برجر', price: 65, cat: 'combos', emoji: '🍔', bg: '#FED7AA', station: 'Hot kitchen', combo: true },
  { id: 30, name: 'Shawarma combo', nameAr: 'وجبة شاورما', price: 45, cat: 'combos', emoji: '🌯', bg: '#FEF3C7', station: 'Hot kitchen', combo: true },
];

export const MOCK_MOD_GROUPS: Record<string, ModifierGroup> = {
  bread: {
    title: 'Bread choice', titleAr: 'نوع الخبز', required: true, min: 1, max: 1,
    options: [
      { name: 'Saj', nameAr: 'صاج', price: 0 },
      { name: 'Pita', nameAr: 'خبز عربي', price: 0 },
      { name: 'Tortilla', nameAr: 'تورتيلا', price: 0 },
      { name: 'Lebanese', nameAr: 'لبناني', price: 2 },
    ],
  },
  cooking: {
    title: 'Cooking', titleAr: 'درجة الاستواء', required: true, min: 1, max: 1,
    options: [
      { name: 'Medium-rare', nameAr: 'نصف ناضج', price: 0 },
      { name: 'Medium', nameAr: 'متوسط', price: 0 },
      { name: 'Well done', nameAr: 'ناضج', price: 0 },
    ],
  },
  size: {
    title: 'Size', titleAr: 'الحجم', required: true, min: 1, max: 1,
    options: [
      { name: 'Small (8")', nameAr: 'صغير', price: 0 },
      { name: 'Medium (12")', nameAr: 'وسط', price: 8 },
      { name: 'Large (14")', nameAr: 'كبير', price: 16 },
      { name: 'Family (16")', nameAr: 'عائلي', price: 24 },
    ],
  },
  addons: {
    title: 'Add-ons', titleAr: 'إضافات', required: false, min: 0, max: 4,
    options: [
      { name: 'Extra garlic sauce', nameAr: 'صوص ثوم', price: 2 },
      { name: 'Extra cheese', nameAr: 'جبنة', price: 5 },
      { name: 'Extra meat', nameAr: 'لحم زيادة', price: 15 },
      { name: 'Mushroom', nameAr: 'فطر', price: 4 },
      { name: 'Avocado', nameAr: 'أفوكادو', price: 6 },
      { name: 'Bacon', nameAr: 'بيكون', price: 8 },
    ],
  },
  toppings: {
    title: 'Extra toppings', titleAr: 'إضافات', required: false, min: 0, max: 5,
    options: [
      { name: 'Mushroom', nameAr: 'فطر', price: 4 },
      { name: 'Olive', nameAr: 'زيتون', price: 3 },
      { name: 'Pepper', nameAr: 'فلفل', price: 3 },
      { name: 'Onion', nameAr: 'بصل', price: 2 },
      { name: 'Extra cheese', nameAr: 'جبنة زيادة', price: 5 },
    ],
  },
  remove: {
    title: 'Remove', titleAr: 'بدون', required: false, min: 0, max: 6,
    options: [
      { name: 'No pickles', nameAr: 'بدون مخلل', price: 0 },
      { name: 'No onions', nameAr: 'بدون بصل', price: 0 },
      { name: 'No tomato', nameAr: 'بدون طماطم', price: 0 },
      { name: 'No lettuce', nameAr: 'بدون خس', price: 0 },
      { name: 'No sauce', nameAr: 'بدون صوص', price: 0 },
      { name: 'No spice', nameAr: 'بدون توابل', price: 0 },
    ],
  },
  protein: {
    title: 'Add protein', titleAr: 'إضافة بروتين', required: false, min: 0, max: 1,
    options: [
      { name: 'Grilled chicken', nameAr: 'دجاج مشوي', price: 12 },
      { name: 'Shrimp', nameAr: 'جمبري', price: 18 },
      { name: 'Beef', nameAr: 'لحم', price: 15 },
    ],
  },
  sides: {
    title: 'Side dish', titleAr: 'طبق جانبي', required: true, min: 1, max: 1,
    options: [
      { name: 'Rice', nameAr: 'أرز', price: 0 },
      { name: 'Fries', nameAr: 'بطاطس', price: 0 },
      { name: 'Salad', nameAr: 'سلطة', price: 0 },
      { name: 'Mashed potato', nameAr: 'بطاطس مهروسة', price: 4 },
    ],
  },
};

export interface PaymentMethodMock {
  id: string;
  name: string;
  sub: string;
  color: string;
  icon: 'card' | 'cash' | 'phone' | 'qr' | 'tag';
}

export const MOCK_PAYMENT_METHODS: PaymentMethodMock[] = [
  { id: 'mada', name: 'Mada', sub: 'Geidea', color: '#10B981', icon: 'card' },
  { id: 'visa', name: 'Visa / MC', sub: 'Tap', color: '#3B82F6', icon: 'card' },
  { id: 'cash', name: 'Cash', sub: 'Bills & coins', color: '#F59E0B', icon: 'cash' },
  { id: 'apple', name: 'Apple Pay', sub: 'via Geidea', color: '#1744A4', icon: 'phone' },
  { id: 'stcpay', name: 'STC Pay', sub: 'Wallet', color: '#8B5CF6', icon: 'qr' },
  { id: 'voucher', name: 'Voucher', sub: 'Code', color: '#EC4899', icon: 'tag' },
];

// ── Customer tier thresholds ─────────────────────────────────────────────
// Kept here (not in @matix/types) because the thresholds are a frontend
// display concern. The backend just returns the resolved tier — these are
// used only for "X pts to next tier" progress bars.

import type { CustomerTier } from '@matix/types';

export const TIER_THRESHOLDS: Record<CustomerTier, { min: number; next: CustomerTier | null; nextAt: number | null }> = {
  new:      { min: 0,    next: 'bronze',   nextAt: 100 },
  bronze:   { min: 100,  next: 'silver',   nextAt: 500 },
  silver:   { min: 500,  next: 'gold',     nextAt: 2000 },
  gold:     { min: 2000, next: 'platinum', nextAt: 5000 },
  platinum: { min: 5000, next: null,       nextAt: null },
};

// ── Constants ────────────────────────────────────────────────────────────

// VAT rate now lives in store/taxConfig.ts (sourced from
// /api/v1/tenant/tax-config → mstr_TaxSetting). SERVICE_RATE stays local
// for the prototype's service-charge helper until service charge gets the
// same treatment.
export const SERVICE_RATE = 0.10;

/**
 * Money formatter — every amount the cashier shows on screen goes through
 * this.
 *
 * The digit count is the TENANT currency's precision, not a hardcoded 2: a
 * KWD item priced 0.136 was being displayed (and, once the same 2 leaked into
 * the payment math, charged) as 0.14. `moneyPrecision()` reads the base
 * currency mirrored out of the print config, and falls back to 2 until that
 * lands — see store/currency.ts.
 */
export function fmt(n: number): string {
  const digits = safePrecision(moneyPrecision());
  return Number(n).toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** Stable avatar color for a customer ID. */
export function avatarColor(id: string | number): string {
  const palette = ['#5478D4', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#0891B2', '#7C3AED'];
  const s = String(id);
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return palette[h % palette.length]!;
}

export function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
}
